import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Image as ImageIcon, Trash2, Pencil, Plus, LogOut, X,
  Calendar, Tag, Save, Loader2, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore } from '../store';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import { uploadToCloudinary } from './AdminSettingsPage';
import LocalizedField from '../components/admin/LocalizedField';
import TranslateAllButton from '../components/admin/TranslateAllButton';
import AdminHeader from '../components/admin/AdminHeader';

// ИСПРАВЛЕНО: Импортируем хук перевода для управления кнопками
import { useTranslation } from '../i18n/translations';

const API = process.env.REACT_APP_BACKEND_URL;
const BLOG_CATEGORIES = [
  { value: 'trends', label: 'Trends' },
  { value: 'news', label: 'News' },
  { value: 'design', label: 'Design' },
  { value: 'craftsmanship', label: 'Craftsmanship' },
  { value: 'interview', label: 'Interview' },
  { value: 'guide', label: 'Guide' },
];

const emptyForm = () => ({
  id: null,
  title: { en: '', nl: '', ro: '', ru: '' },
  excerpt: { en: '', nl: '', ro: '', ru: '' },
  content: { en: '', nl: '', ro: '', ru: '' },
  category: 'trends',
  tags: '',
  author: 'FEDYK Editorial',
  featured_image: null,
  featured_image_public_id: null,
  is_published: false,
  slug: '',
});

const JournalFormModal = ({ open, onClose, token, initial, onSaved }) => {
  // ИСПРАВЛЕНО: Подключаем хук локализации внутри модального окна
  const { t } = useTranslation();

  const initialForm = () => {
    if (!initial) return emptyForm();
    return {
      id: initial.id,
      title: { en: '', nl: '', ro: '', ru: '', ...(initial.title || {}) },
      excerpt: { en: '', nl: '', ro: '', ru: '', ...(initial.excerpt || {}) },
      content: { en: '', nl: '', ro: '', ru: '', ...(initial.content || {}) },
      category: initial.category || 'trends',
      tags: (initial.tags || []).join(', '),
      author: initial.author || 'FEDYK Editorial',
      featured_image: initial.featured_image || null,
      featured_image_public_id: initial.featured_image_public_id || null,
      is_published: !!initial.is_published,
      slug: initial.slug || '',
    };
  };
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const up = await uploadToCloudinary(file, token, 'blog', 'image');
      setForm((p) => ({ ...p, featured_image: up.secure_url, featured_image_public_id: up.public_id }));
      toast.success('Cover image uploaded');
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setForm((p) => ({ ...p, featured_image: null, featured_image_public_id: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.en.trim()) { toast.error('Title (English) is required'); return; }
    setSaving(true);
    try {
      const url = form.id ? `${API}/api/admin/journal/${form.id}` : `${API}/api/admin/journal`;
      const method = form.id ? 'PUT' : 'POST';
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        content: form.content,
        category: form.category,
        tags: form.tags.split(',').map((s) => s.trim()).filter(Boolean),
        author: form.author || 'FEDYK Editorial',
        featured_image: form.featured_image,
        featured_image_public_id: form.featured_image_public_id,
        is_published: form.is_published,
      };
      if (form.slug.trim()) payload.slug = form.slug.trim();
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success(form.id ? 'Article updated' : 'Article created');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-background w-full max-w-3xl max-h-[92vh] overflow-y-auto border border-border rounded-none shadow-2xl"
        data-testid="journal-form-modal"
      >
        <div className="sticky top-0 z-10 bg-background border-b border-border px-8 py-5 flex items-start justify-between">
          <div>
            <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground mb-1.5">Journal</p>
            <h2 className="text-2xl font-extralight tracking-tight text-gray-900">
              {form.id ? 'Edit Article' : 'New Article'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted" aria-label="Close" data-testid="journal-form-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
          <div className="flex justify-end -mb-1">
            <TranslateAllButton
              testid="journal-translate-all"
              fields={() => [
                { value: form.title, onChange: (v) => setForm((p) => ({ ...p, title: v })) },
                { value: form.excerpt, onChange: (v) => setForm((p) => ({ ...p, excerpt: v })) },
                { value: form.content, onChange: (v) => setForm((p) => ({ ...p, content: v })) },
              ]}
            />
          </div>
          
          <LocalizedField
            label="Title" required
            value={form.title}
            onChange={(next) => setForm((p) => ({ ...p, title: next }))}
            testid="journal-title"
          />

          <LocalizedField
            label="Excerpt"
            multiline rows={3}
            value={form.excerpt}
            onChange={(next) => setForm((p) => ({ ...p, excerpt: next }))}
            testid="journal-excerpt"
          />

          <LocalizedField
            label="Content (HTML allowed; sanitized on render)"
            multiline rows={10}
            value={form.content}
            onChange={(next) => setForm((p) => ({ ...p, content: next }))}
            testid="journal-content"
            placeholder="<p>Write your article here…</p>"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Category *</label>
              <select
                value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 border border-border text-sm cursor-pointer font-medium rounded-none h-11 focus:outline-none focus:border-black"
                style={{ color: '#000000', backgroundColor: '#ffffff' }}
                data-testid="journal-category"
              >
                <option value="trends" style={{ color: '#000000', backgroundColor: '#ffffff' }}>Trends</option>
                <option value="news" style={{ color: '#000000', backgroundColor: '#ffffff' }}>News</option>
                <option value="design" style={{ color: '#000000', backgroundColor: '#ffffff' }}>Design</option>
                <option value="craftsmanship" style={{ color: '#000000', backgroundColor: '#ffffff' }}>Craftsmanship</option>
                <option value="interview" style={{ color: '#000000', backgroundColor: '#ffffff' }}>Interview</option>
                <option value="guide" style={{ color: '#000000', backgroundColor: '#ffffff' }}>Guide</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Author</label>
              <input
                type="text" value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                className="w-full px-3 py-2 border border-border bg-background text-foreground text-sm rounded-none h-11 focus:outline-none focus:border-black"
                data-testid="journal-author"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Tags (comma-separated)</label>
              <input
                type="text" value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="design, oak, hidden-mount"
                className="w-full px-3 py-2 border border-border bg-background text-foreground text-sm rounded-none h-11 focus:outline-none focus:border-black"
                data-testid="journal-tags"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Slug (optional — auto-generated)</label>
            <input
              type="text" value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="oak-finishes-2026"
              className="w-full px-3 py-2 border border-border bg-background text-foreground text-sm font-mono rounded-none h-11 focus:outline-none focus:border-black"
              data-testid="journal-slug"
            />
          </div>

          {/* Cover image */}
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Cover Image</label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImage} />
            {form.featured_image ? (
              <div className="border border-border bg-white overflow-hidden flex flex-col max-w-md" data-testid="journal-cover-preview">
                <div className="w-full aspect-video overflow-hidden flex items-center justify-center bg-muted">
                  <img src={form.featured_image} alt="cover" className="w-full h-full object-cover" />
                </div>
                <div className="p-2.5 bg-gray-50 border-t border-border flex items-center justify-between gap-3">
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-white text-gray-900 border-gray-200 h-9 rounded-none text-xs px-4">
                    <Upload className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />Replace Image
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={handleRemoveImage} className="bg-white border-gray-200 text-destructive hover:bg-destructive/5 h-9 w-9 p-0 rounded-none flex items-center justify-center">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full max-w-md aspect-video border border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-foreground hover:text-foreground bg-background rounded-none"
                data-testid="journal-cover-empty"
              >
                <ImageIcon className="w-8 h-8 mb-2" strokeWidth={1} />
                <span className="text-xs">{uploading ? 'Uploading…' : 'Click to upload cover'}</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
              <input
                type="checkbox" checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                data-testid="journal-published"
                className="accent-black w-4 h-4"
              />
              Published
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none h-11 px-5 text-xs">
              {t?.common?.cancel || 'Cancel'}
            </Button>
            <Button type="submit" disabled={saving} className="btn-primary min-w-[140px] rounded-none h-11 text-xs" data-testid="journal-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> : <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />}
              {saving ? 'Saving…' : form.id ? (t?.account?.saveChanges || 'Save Changes') : (t?.common?.submit || 'Create')}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const AdminJournalPage = () => {
  const navigate = useNavigate();
  const userFromHook = useAuthStore((s) => s.user);
  const tokenFromHook = useAuthStore((s) => s.token);
  const logout = useAuthStore((s) => s.logout);
  const [hydrated, setHydrated] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => setHydrated(true), 300);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const u = useAuthStore.getState().user;
    if (!u || u.role !== 'admin') {
      navigate('/admin', { replace: true, state: { from: '/admin/journal' } });
    }
  }, [hydrated, navigate]);

  const fetchPosts = React.useCallback(async () => {
    if (!tokenFromHook) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/journal`, { headers: { Authorization: `Bearer ${tokenFromHook}` } });
      if (res.status === 401 || res.status === 403) { logout(); navigate('/admin'); return; }
      const data = await res.json();
      setPosts(data.posts || []);
    } catch {
      toast.error('Failed to load journal articles');
    } finally {
      setLoading(false);
    }
  }, [tokenFromHook, logout, navigate]);

  useEffect(() => { if (hydrated) fetchPosts(); }, [hydrated, fetchPosts]);

  const handleNew = () => { setEditing(null); setModalOpen(true); };
  const handleEdit = (post) => { setEditing(post); setModalOpen(true); };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${(post.title?.en) || post.slug}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/journal/${post.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${tokenFromHook}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Article deleted');
      fetchPosts();
    } catch {
      toast.error('Failed to delete article');
    }
  };

  if (!hydrated) return <div className="min-h-screen flex items-center justify-center text-xs text-gray-400 bg-background">Loading…</div>;
  if (!userFromHook || userFromHook.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-background text-left font-light" data-testid="admin-journal-page">
      <SEO title="Journal · Admin" description="Manage journal articles" path="admin/journal" />

      {/* ИСПРАВЛЕНО: Статичный дублирующий заголовок заменен на глобальный компонент шапки */}
      <AdminHeader active="journal" />

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-caption text-muted-foreground mb-3 text-xs tracking-wider uppercase text-gray-400 font-semibold">CMS · Editorial</p>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-gray-900">Journal</h1>
            <p className="text-muted-foreground mt-3 max-w-xl text-sm leading-relaxed text-gray-500">
              Write and manage articles displayed publicly on the blog platform feed <code className="bg-muted px-1.5 py-0.5">/blog</code>.
            </p>
          </div>
          
          {/* ИСПРАВЛЕНО: Кнопки Refresh и New Article приведены к общему дизайну variant, shadow, hover-эффектам и жестко зафиксированы на h-10 (40px) */}
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchPosts} 
              disabled={loading} 
              className="h-10 bg-white text-gray-900 border-gray-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium"
              style={{ height: '40px' }}
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />Refresh
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleNew} 
              className="h-10 bg-white text-gray-900 border-gray-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium"
              style={{ height: '40px' }}
              data-testid="admin-new-journal-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />New Article
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-muted-foreground text-xs">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="py-24 text-center text-muted-foreground border border-border bg-white rounded-none shadow-sm text-xs" data-testid="admin-journal-empty">
            <ImageIcon className="w-10 h-10 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
            <p className="text-muted-foreground mb-6">No articles yet — write your first one.</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleNew} 
              className="h-10 bg-white text-gray-900 border-gray-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium mx-auto"
              style={{ height: '40px' }}
            >
              <Plus className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />New Article
            </Button>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border bg-white rounded shadow-sm" data-testid="admin-journal-list">
            {posts.map((p) => (
              <div key={p.id} className="flex items-center gap-5 p-4 hover:bg-neutral-50/50 transition-colors" data-testid={`admin-journal-${p.id}`}>
                <div className="w-20 h-14 bg-muted overflow-hidden flex-shrink-0 border border-gray-100 rounded">
                  {p.featured_image ? (
                    <img src={p.featured_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <ImageIcon className="w-5 h-5" strokeWidth={1.2} />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate text-sm">{p.title?.en || p.slug}</p>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground font-normal">
                    <span className="capitalize px-1.5 py-0.5 bg-gray-100 rounded text-[10px] text-gray-600 font-medium">{p.category}</span>
                    {p.published_at && (
                      <span className="flex items-center gap-1 text-gray-500">
                        <Calendar className="w-3 h-3 text-gray-400" strokeWidth={1.5} />
                        {new Date(p.published_at).toLocaleDateString()}
                      </span>
                    )}
                    {p.tags?.length > 0 && (
                      <span className="flex items-center gap-1 truncate max-w-[200px] text-gray-500">
                        <Tag className="w-3 h-3 text-gray-400" strokeWidth={1.5} />{p.tags.join(', ')}
                      </span>
                    )}
                    {!p.is_published && <span className="px-1.5 py-0.5 bg-neutral-900 text-white text-[9px] tracking-wider uppercase font-bold rounded-sm">Draft</span>}
                  </div>
                </div>

                {/* ИСПРАВЛЕНО: Кнопки действий приведены к единому квадратному стилю h-9 / w-11 с эффектом hover */}
                <div className="flex items-center gap-2 shrink-0">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleEdit(p)} 
                    className="rounded-none text-xs h-9 bg-white text-gray-900 border-gray-200 transition-colors p-0 w-11"
                    data-testid={`admin-journal-edit-${p.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDelete(p)} 
                    className="rounded-none text-xs h-9 bg-white text-destructive border-gray-200 transition-colors p-0 w-11 hover:bg-rose-50" 
                    data-testid={`admin-journal-delete-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {modalOpen && (
          <JournalFormModal
            key={editing?.id || 'new'}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            token={tokenFromHook}
            initial={editing}
            onSaved={fetchPosts}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminJournalPage;
